/**
 * Utility functions for handling cookies
 */

/**
 * Gets the value of a specified cookie
 * @param {string} cookieName - The name of the cookie to retrieve
 * @returns {string|null} The cookie value if found, null otherwise
 */
export const getCookie = (cookieName) => {
  const cookies = document.cookie.split(";");
  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i].trim();
    // Check if this cookie starts with the name we're looking for
    if (cookie.startsWith(cookieName + "=")) {
      // Return the value part (after the '=')
      return cookie.substring(cookieName.length + 1);
    }
  }
  return null; // Cookie not found
};

/**
 * Sets a cookie with the provided name and value
 * @param {string} cookieName - The name of the cookie to set
 * @param {string} cookieValue - The value to store in the cookie
 * @param {number} [expiryDays=30] - Number of days until cookie expiration
 * @param {string} [path='/'] - The path for the cookie
 * @param {boolean} [secure=false] - Whether the cookie should be secure (HTTPS only)
 * @param {string} [sameSite='strict'] - SameSite policy (strict, lax, or none)
 */
export const setCookie = (
  cookieName,
  cookieValue,
  expiryDays = 30,
  path = "/",
  secure = false,
  sameSite = "strict"
) => {
  // Create a new date for expiration
  const expiryDate = new Date();
  // Handle fractional days by converting to milliseconds
  const expiryMs = expiryDays * 24 * 60 * 60 * 1000; // Convert days to milliseconds
  expiryDate.setTime(expiryDate.getTime() + expiryMs);

  // Build the cookie string
  let cookieString = `${cookieName}=${cookieValue}`;
  cookieString += `; expires=${expiryDate.toUTCString()}`;
  cookieString += `; path=${path}`;

  if (sameSite) {
    cookieString += `; samesite=${sameSite}`;
  }

  if (secure) {
    cookieString += "; secure";
  }

  // Set the cookie
  document.cookie = cookieString;
};

/**
 * Removes a cookie by setting its expiration to the past
 * @param {string} cookieName - The name of the cookie to remove
 * @param {string} [path='/'] - The path for the cookie
 */
export const deleteCookie = (cookieName, path = "/") => {
  document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}`;
};

/**
 * Gets the session ID from cookies
 * @returns {string|null} The session ID if found, null otherwise
 */
export const getSessionId = () => {
  return getCookie("sessionId");
};

/**
 * Sets the session ID cookie with secure defaults
 * @param {string} sessionId - The session ID to store
 * @param {number} [expiryDays=1] - Number of days until cookie expiration (default 1 day to match backend session timeout)
 */
export const setSessionId = (sessionId, expiryDays = 1) => {
  setCookie("sessionId", sessionId, expiryDays, "/", false, "lax");
};

/**
 * Clears the session ID cookie
 */
export const clearSessionId = () => {
  deleteCookie("sessionId");
};

/**
 * Sets a cookie with expiration time specified in minutes (more readable than fractional days)
 * @param {string} cookieName - The name of the cookie to set
 * @param {string} cookieValue - The value to store in the cookie
 * @param {number} expiryMinutes - Number of minutes until cookie expiration
 * @param {string} [path='/'] - The path for the cookie
 * @param {boolean} [secure=false] - Whether the cookie should be secure (HTTPS only)
 * @param {string} [sameSite='lax'] - SameSite policy (strict, lax, or none)
 */
export const setCookieMinutes = (
  cookieName,
  cookieValue,
  expiryMinutes,
  path = "/",
  secure = false,
  sameSite = "lax"
) => {
  // Convert minutes to days for setCookie function
  const expiryDays = expiryMinutes / 1440; // 1440 minutes in a day
  setCookie(cookieName, cookieValue, expiryDays, path, secure, sameSite);
};
